import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import Loader from 'react-loader-spinner';
import { FiSave } from 'react-icons/fi';
import { API_URLS } from '../constants/config';
import { useI18n } from '../utilities/i18n';
import { authorizedPost, getApiErrorMessage } from '../utilities/stadiumsApi';
import { useChampionships } from '../utilities/championships';
import { useAdminAccess } from '../utilities/adminAccess';
import { champFields, extractEntity, extractList } from '../utilities/tournamentsApi';
import {
  WEEKDAYS,
  extractWindow,
  extractWindows,
  getBrowserTimezoneOffset,
  isTransferWindowConfigured,
  isTransferWindowEnabled,
  normalizeWeekday,
  parseTimeParts,
  toTimePayload,
  weekdayLabelKey,
  windowFormFromLeague,
} from '../utilities/transferWindows';
import CustomModal from '../components/CustomModal';
import StadiumCrumbs from '../components/StadiumCrumbs';
import '../styles/createMatchModal.css';
import '../styles/stadiumsManagement.css';
import '../styles/disqualificationsManagement.css';
import '../styles/tournamentsManagement.css';
import '../styles/transferWindows.css';

const emptyForm = {
  country_id: '',
  league_id: '',
  enabled: true,
  dayFrom: '',
  timeFrom: '',
  dayTo: '',
  timeTo: '',
  timezoneOffset: null,
};

export default function ManageTransferWindow() {
  const { leagueId } = useParams();
  const isNew = !leagueId;
  const dispatch = useDispatch();
  const history = useHistory();
  const { t } = useI18n();
  const { champId, loading: champsLoading } = useChampionships();
  const { canWrite } = useAdminAccess('leagues');

  const [form, setForm] = useState(emptyForm);
  const [leagueName, setLeagueName] = useState('');
  const [configured, setConfigured] = useState(false);
  const [countries, setCountries] = useState([]);
  const [availableLeagues, setAvailableLeagues] = useState([]);
  const [countryLeagues, setCountryLeagues] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [loadingCountryLeagues, setLoadingCountryLeagues] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteVisible, setDeleteVisible] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });

  useEffect(() => {
    if (!champId && champsLoading) return undefined;
    initPage();
    return undefined;
  }, [leagueId, champId, champsLoading]);

  const availableIds = useMemo(
    () => new Set(availableLeagues.map((item) => Number(item.id))),
    [availableLeagues]
  );

  const leaguesForCountry = useMemo(
    () => countryLeagues.filter((item) => availableIds.has(Number(item.id))),
    [countryLeagues, availableIds]
  );

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) {
      setErrors((prev) => ({ ...prev, [key]: '' }));
    }
  };

  const loadCountries = async () => {
    setLoadingCountries(true);
    try {
      const response = await authorizedPost(dispatch, API_URLS.getAdminCountries, champFields(champId));
      const list = extractList(response.data).map((item) => ({
        id: item.id,
        name: item.name || item.country_name || '—',
      }));
      setCountries(list);
      return list;
    } catch (error) {
      setCountries([]);
      throw error;
    } finally {
      setLoadingCountries(false);
    }
  };

  const loadLeaguesForCountry = async (countryId) => {
    if (!countryId) {
      setCountryLeagues([]);
      return [];
    }
    setLoadingCountryLeagues(true);
    try {
      const response = await authorizedPost(dispatch, API_URLS.getAdminCountry, { id: Number(countryId) });
      const leagues = extractList(extractEntity(response.data).leagues).map((item) => ({
        id: item.id || item.league_id,
        name: item.name || item.league_name || '—',
      }));
      setCountryLeagues(leagues);
      return leagues;
    } catch (error) {
      setCountryLeagues([]);
      throw error;
    } finally {
      setLoadingCountryLeagues(false);
    }
  };

  const initPage = async () => {
    setMessage({ text: '', type: '' });
    setErrors({});
    if (!champId) {
      setLoading(false);
      setMessage({ text: t('twLoadError'), type: 'error' });
      return;
    }

    setLoading(true);
    try {
      if (isNew) {
        const [windowsResponse] = await Promise.all([
          authorizedPost(dispatch, API_URLS.getAdminTransferWindows, { champ_id: champId }),
          loadCountries(),
        ]);
        const options = extractWindows(windowsResponse.data).filter((item) => !isTransferWindowEnabled(item));
        setAvailableLeagues(options);
        setCountryLeagues([]);
        setForm({ ...emptyForm, timezoneOffset: getBrowserTimezoneOffset() });
        setLeagueName('');
        setConfigured(false);
      } else {
        const response = await authorizedPost(dispatch, API_URLS.getAdminTransferWindow, {
          id: Number(leagueId),
          champ_id: champId,
        });
        const current = extractWindow(response.data);
        if (!current) {
          setMessage({ text: t('twNotFound'), type: 'error' });
          setForm(emptyForm);
          setLeagueName('');
          setConfigured(false);
        } else {
          setLeagueName(current.name || '');
          setForm({ ...windowFormFromLeague(current), country_id: '' });
          setConfigured(isTransferWindowConfigured(current));
        }
        setAvailableLeagues([]);
        setCountries([]);
        setCountryLeagues([]);
      }
    } catch (error) {
      setMessage({ text: getApiErrorMessage(error, t('twLoadError')), type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleCountryChange = async (countryId) => {
    updateField('country_id', countryId);
    setForm((prev) => ({
      ...prev,
      country_id: countryId,
      league_id: '',
      dayFrom: '',
      timeFrom: '',
      dayTo: '',
      timeTo: '',
      enabled: true,
      timezoneOffset: prev.timezoneOffset ?? getBrowserTimezoneOffset(),
    }));
    setErrors((prev) => ({ ...prev, country_id: '', league_id: '' }));
    setMessage({ text: '', type: '' });
    try {
      await loadLeaguesForCountry(countryId);
    } catch (error) {
      setMessage({ text: getApiErrorMessage(error, t('tsLoadLeaguesError')), type: 'error' });
    }
  };

  const handleLeagueChange = (nextId) => {
    if (!nextId) {
      updateField('league_id', '');
      return;
    }
    const selected = availableLeagues.find((item) => String(item.id) === String(nextId));
    if (!selected) {
      updateField('league_id', nextId);
      return;
    }
    const nextForm = windowFormFromLeague(selected);
    setForm((prev) => ({
      ...nextForm,
      country_id: prev.country_id,
      enabled: true,
      timezoneOffset: nextForm.timezoneOffset ?? getBrowserTimezoneOffset(),
    }));
    if (errors.league_id) setErrors((prev) => ({ ...prev, league_id: '' }));
  };

  const validate = () => {
    const nextErrors = {};
    if (isNew && !form.country_id) nextErrors.country_id = t('twNeedCountry');
    if (isNew && !form.league_id) nextErrors.league_id = t('twNeedLeague');
    if (normalizeWeekday(form.dayFrom) == null) nextErrors.dayFrom = t('twNeedDay');
    if (normalizeWeekday(form.dayTo) == null) nextErrors.dayTo = t('twNeedDay');
    if (!parseTimeParts(form.timeFrom)) nextErrors.timeFrom = t('twNeedTime');
    if (!parseTimeParts(form.timeTo)) nextErrors.timeTo = t('twNeedTime');
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    setMessage({ text: '', type: '' });
    try {
      const nextLeagueId = Number(isNew ? form.league_id : leagueId);
      const timezoneOffset = Number.isFinite(Number(form.timezoneOffset))
        ? Number(form.timezoneOffset)
        : getBrowserTimezoneOffset();
      const response = await authorizedPost(dispatch, API_URLS.changeAdminTransferWindow, {
        champ_id: champId,
        league_id: nextLeagueId,
        windowSchedulerEnabled: form.enabled ? 1 : 0,
        windowSchedulerDayFrom: normalizeWeekday(form.dayFrom),
        windowSchedulerTimeFrom: toTimePayload(form.timeFrom),
        windowSchedulerDayTo: normalizeWeekday(form.dayTo),
        windowSchedulerTimeTo: toTimePayload(form.timeTo),
        windowSchedulerTimezoneOffset: timezoneOffset,
      });
      if (isNew) {
        history.push('/transfer_windows');
      } else {
        const updated = extractWindow(response.data);
        if (updated) {
          setLeagueName(updated.name || leagueName);
          setForm({ ...windowFormFromLeague(updated), country_id: '' });
          setConfigured(isTransferWindowConfigured(updated));
        }
        setMessage({ text: t('twSaved'), type: 'success' });
      }
    } catch (error) {
      setMessage({ text: getApiErrorMessage(error, t('twSaveError')), type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await authorizedPost(dispatch, API_URLS.removeAdminTransferWindow, {
        champ_id: champId,
        league_id: Number(leagueId),
      });
      history.push('/transfer_windows');
    } catch (error) {
      setDeleteVisible(false);
      setMessage({ text: getApiErrorMessage(error, t('twRemoveError')), type: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  const weekdayOptions = WEEKDAYS.map((day) => (
    <option key={day} value={day}>{t(weekdayLabelKey(day))}</option>
  ));

  const canCreate = Boolean(form.country_id) && leaguesForCountry.length > 0;

  if (loading) {
    return (
      <div className="layoutWrapper st-layout">
        <div className="matchCenterWrapper st-page">
          <div className="matchCenterHeader">
            <div className="st-header">
              <StadiumCrumbs
                items={[
                  { label: t('navTransferWindows'), to: '/transfer_windows' },
                  { label: isNew ? t('twNew') : t('twEdit') },
                ]}
              />
            </div>
          </div>
          <div className="st-loading">
            <Loader type="Oval" color="#3ad1a0" height={48} width={48} />
            <p>{t('twLoading')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="layoutWrapper st-layout">
      <div className="matchCenterWrapper st-page tw-edit-page">
        <div className="matchCenterHeader">
          <div className="st-header">
            <StadiumCrumbs
              items={[
                { label: t('navTransferWindows'), to: '/transfer_windows' },
                { label: isNew ? t('twNew') : (leagueName || t('twEdit')) },
              ]}
            />
          </div>
        </div>

        <div className="st-scroll">
          <div className="st-content">
            {message.text ? <div className={`st-message ${message.type}`}>{message.text}</div> : null}

            <div className="st-block">
              <div className="st-form-grid">
                <div className="st-field st-field-wide">
                  <label className="st-check">
                    <input
                      type="checkbox"
                      checked={form.enabled}
                      onChange={(event) => updateField('enabled', event.target.checked)}
                    />
                    {t('twEnabled')}
                  </label>
                </div>

                {isNew ? (
                  <>
                    <div className="st-field st-field-wide">
                      <label>{t('gmCountry')} <span className="st-required">*</span></label>
                      {loadingCountries ? (
                        <p className="st-hint">{t('twLoading')}</p>
                      ) : countries.length ? (
                        <select
                          className={`st-select ${errors.country_id ? 'error' : ''}`}
                          value={form.country_id}
                          onChange={(event) => handleCountryChange(event.target.value)}
                        >
                          <option value="">{t('cmCountryPh')}</option>
                          {countries.map((item) => (
                            <option key={item.id} value={item.id}>{item.name}</option>
                          ))}
                        </select>
                      ) : (
                        <p className="st-hint">{t('twNoCountries')}</p>
                      )}
                      {errors.country_id ? <span className="st-field-error">{errors.country_id}</span> : null}
                    </div>

                    <div className="st-field st-field-wide">
                      <label>{t('twLeague')} <span className="st-required">*</span></label>
                      {!form.country_id ? (
                        <p className="st-hint">{t('twPickCountryFirst')}</p>
                      ) : loadingCountryLeagues ? (
                        <p className="st-hint">{t('twLoading')}</p>
                      ) : leaguesForCountry.length ? (
                        <select
                          className={`st-select ${errors.league_id ? 'error' : ''}`}
                          value={form.league_id}
                          onChange={(event) => handleLeagueChange(event.target.value)}
                        >
                          <option value="">{t('twLeaguePh')}</option>
                          {leaguesForCountry.map((item) => (
                            <option key={item.id} value={item.id}>{item.name}</option>
                          ))}
                        </select>
                      ) : (
                        <p className="st-hint">{t('twNoLeaguesInCountry')}</p>
                      )}
                      {errors.league_id ? <span className="st-field-error">{errors.league_id}</span> : null}
                    </div>
                  </>
                ) : (
                  <div className="st-field st-field-wide">
                    <label>{t('twLeague')}</label>
                    <input className="st-input" value={leagueName} readOnly />
                  </div>
                )}

                <div className="st-field">
                  <label>{t('twDayFrom')} <span className="st-required">*</span></label>
                  <select
                    className={`st-select ${errors.dayFrom ? 'error' : ''}`}
                    value={form.dayFrom}
                    onChange={(event) => updateField('dayFrom', event.target.value)}
                  >
                    <option value="">{t('twDayPh')}</option>
                    {weekdayOptions}
                  </select>
                  {errors.dayFrom ? <span className="st-field-error">{errors.dayFrom}</span> : null}
                </div>

                <div className="st-field">
                  <label>{t('twTimeFrom')} <span className="st-required">*</span></label>
                  <div className="st-native-dt">
                    <input
                      type="time"
                      className={`st-input ${errors.timeFrom ? 'error' : ''}`}
                      value={form.timeFrom}
                      onChange={(event) => updateField('timeFrom', event.target.value)}
                    />
                  </div>
                  {errors.timeFrom ? <span className="st-field-error">{errors.timeFrom}</span> : null}
                </div>

                <div className="st-field">
                  <label>{t('twDayTo')} <span className="st-required">*</span></label>
                  <select
                    className={`st-select ${errors.dayTo ? 'error' : ''}`}
                    value={form.dayTo}
                    onChange={(event) => updateField('dayTo', event.target.value)}
                  >
                    <option value="">{t('twDayPh')}</option>
                    {weekdayOptions}
                  </select>
                  {errors.dayTo ? <span className="st-field-error">{errors.dayTo}</span> : null}
                </div>

                <div className="st-field">
                  <label>{t('twTimeTo')} <span className="st-required">*</span></label>
                  <div className="st-native-dt">
                    <input
                      type="time"
                      className={`st-input ${errors.timeTo ? 'error' : ''}`}
                      value={form.timeTo}
                      onChange={(event) => updateField('timeTo', event.target.value)}
                    />
                  </div>
                  {errors.timeTo ? <span className="st-field-error">{errors.timeTo}</span> : null}
                </div>
              </div>
            </div>
          </div>
        </div>

        {canWrite ? (
          <div className={`st-actions ${!isNew ? 'st-actions-split' : ''}`}>
            {!isNew && configured ? (
              <button
                type="button"
                className="st-btn st-btn-danger"
                disabled={saving || deleting}
                onClick={() => setDeleteVisible(true)}
              >
                {t('delete')}
              </button>
            ) : <span />}
            <button
              type="button"
              className="st-btn st-btn-primary"
              disabled={saving || (isNew && !canCreate)}
              onClick={handleSave}
            >
              <FiSave /> {saving ? t('twLoading') : (isNew || !configured ? t('twCreate') : t('stdSaveChanges'))}
            </button>
          </div>
        ) : null}
      </div>

      <CustomModal
        styles={{ width: 420, height: 'auto', paddingLeft: 32, paddingRight: 32 }}
        visible={deleteVisible}
        closeModal={() => setDeleteVisible(false)}
      >
        <div className="st-delete-modal">
          <div className="customModalHeader">{t('twDeleteTitle')}</div>
          <p>{t('twDeleteText')}</p>
          <div className="st-delete-actions">
            <button type="button" className="btn-modal btn-secondary-modal" onClick={() => setDeleteVisible(false)}>
              {t('cancel')}
            </button>
            <button type="button" className="btn-modal btn-danger-modal" disabled={deleting} onClick={handleDelete}>
              {t('delete')}
            </button>
          </div>
        </div>
      </CustomModal>
    </div>
  );
}
